import React, { useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, publicPdfUrl } from "@/lib/api";
import { toast } from "sonner";
import { FileText, Download, MessageCircle, CheckCircle2, Plus } from "lucide-react";
import BulkGenerate from "@/components/BulkGenerate";

const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const PAYMENT_MODES = ["UPI","Bank Transfer","Cash","Credit Card"];

function todayStr() { return new Date().toISOString().slice(0, 10); }
function currentMonthYear() { const d = new Date(); return `${MONTHS[d.getMonth()]} ${d.getFullYear()}`; }

const MONTH_BOUNDS = (() => {
  const d = new Date();
  const fmt = (x) => `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}`;
  return { min: fmt(new Date(d.getFullYear(), d.getMonth() - 3, 1)), max: fmt(new Date(d.getFullYear(), d.getMonth() + 1, 1)) };
})();
const toMonthValue = (my) => {
  const parts = (my || "").split(" ");
  const idx = MONTHS.indexOf(parts[0]);
  return idx >= 0 && parts[1] ? `${parts[1]}-${String(idx + 1).padStart(2, "0")}` : "";
};
const fromMonthValue = (v) => {
  const [y, m] = v.split("-");
  return `${MONTHS[parseInt(m, 10) - 1]} ${y}`;
};

export default function GenerateReceiptTab({ tenants, owner, onGenerated }) {
  const [tenantId, setTenantId] = useState("");
  const [monthYear, setMonthYear] = useState(currentMonthYear());
  const [amount, setAmount] = useState("");
  const [mode, setMode] = useState("UPI");
  const [date, setDate] = useState(todayStr());
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [view, setView] = useState("single");

  const selectedTenant = useMemo(() => tenants.find((t) => t.id === tenantId), [tenants, tenantId]);
  const sortedTenants = useMemo(() => [...tenants].sort((a, b) => {
    const ra = parseInt(a.room_no, 10);
    const rb = parseInt(b.room_no, 10);
    if (!isNaN(ra) && !isNaN(rb) && ra !== rb) return ra - rb;
    if (!isNaN(ra) && isNaN(rb)) return -1;
    if (isNaN(ra) && !isNaN(rb)) return 1;
    return String(a.room_no).localeCompare(String(b.room_no));
  }), [tenants]);

  const onPickTenant = (id) => {
    setTenantId(id);
    const t = tenants.find((x) => x.id === id);
    if (t) setAmount(t.monthly_rent ? String(t.monthly_rent) : "");
  };

  const submit = async () => {
    if (!tenantId) return toast.error("Select a tenant");
    if (!amount) return toast.error("Enter amount");
    if (date > todayStr()) return toast.error("Payment date cannot be in the future");
    setBusy(true);
    try {
      const r = await api.post("/receipts", {
        tenant_id: tenantId, month_year: monthYear,
        amount_paid: parseFloat(amount), payment_mode: mode, payment_date: date, notes,
      });
      setResult(r.data);
      toast.success("Receipt generated & uploaded!");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to generate receipt"); }
    finally { setBusy(false); }
  };

  const buildWaLink = (receipt) => {
    const backend = process.env.REACT_APP_BACKEND_URL;
    const link = `${backend}${receipt.pdf_url}`;
    const msg = `Hi ${receipt.tenant_name},\n\nHere is your rent receipt from ${owner?.property_name} for ${receipt.month_year}.\n\n• Receipt #: ${receipt.receipt_no}\n• Amount Paid: ₹ ${Number(receipt.amount_paid).toLocaleString("en-IN")}\n• Mode: ${receipt.payment_mode}\n• Date: ${receipt.payment_date}\n\nDownload PDF: ${link}\n\nThank you!\n— ${owner?.property_name}`;
    const num = (receipt.tenant_whatsapp || "").replace(/[^0-9]/g, "");
    return `https://wa.me/${num}?text=${encodeURIComponent(msg)}`;
  };

  const onWhatsApp = () => {
    if (!result) return;
    window.open(buildWaLink(result), "_blank");
    toast.success("WhatsApp opened with tenant number pre-filled");
  };

  const onDownload = () => {
    if (!result) return;
    const backend = process.env.REACT_APP_BACKEND_URL;
    window.open(`${backend}${result.pdf_url}`, "_blank");
  };

  const reset = () => {
    setResult(null); setTenantId(""); setAmount(""); setNotes("");
    onGenerated?.();
  };

  return (
    <Card className="p-6 border-slate-200">
      <div className="mb-5">
        <h2 className="font-display text-xl font-semibold flex items-center gap-2"><FileText className="h-5 w-5 text-indigo-600" /> Generate rent eReceipt</h2>
        <p className="text-sm text-slate-500 mt-0.5">Pick a tenant, review numbers, then share to WhatsApp in one click.</p>
        <div className="mt-3 inline-flex rounded-lg border border-slate-200 p-0.5 bg-slate-50">
          {[["single", "Single receipt"], ["bulk", "Bulk generate"]].map(([k, l]) => (
            <button key={k} onClick={() => setView(k)} data-testid={`receipt-mode-${k}-button`}
              className={`px-3 py-1.5 text-sm rounded-md ${view === k ? "bg-white shadow-sm text-indigo-700 font-medium" : "text-slate-500"}`}>{l}</button>
          ))}
        </div>
      </div>

      {view === "bulk" ? (
        <BulkGenerate tenants={tenants} defaultMonth={currentMonthYear()} today={todayStr()} toMonthValue={toMonthValue}
          fromMonthValue={fromMonthValue} bounds={MONTH_BOUNDS} onGenerated={onGenerated} />
      ) : result ? (
        <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-6" data-testid="receipt-result-card">
          <div className="flex items-center gap-2 text-emerald-700">
            <CheckCircle2 className="h-5 w-5" />
            <span className="font-semibold">Receipt {result.receipt_no} created for {result.tenant_name}</span>
          </div>
          <p className="mt-2 text-sm text-slate-700">
            ₹ {Number(result.amount_paid).toLocaleString("en-IN")} for {result.month_year} • {result.payment_mode}
          </p>
          <div className="mt-5 flex flex-wrap gap-2">
            <a href={`${process.env.REACT_APP_BACKEND_URL}${result.pdf_url}`} target="_blank" rel="noopener noreferrer" data-testid="receipt-download-pdf-button">
              <Button className="bg-indigo-600 hover:bg-indigo-700 text-white" onClick={(e) => { e.preventDefault(); onDownload(); }}>
                <Download className="h-4 w-4 mr-1.5" /> Download PDF
              </Button>
            </a>
            <Button className="bg-[#25D366] hover:bg-[#1ebd59] text-white" onClick={onWhatsApp} data-testid="receipt-whatsapp-share-button">
              <MessageCircle className="h-4 w-4 mr-1.5" /> Send to tenant WhatsApp
            </Button>
            <Button className="bg-amber-500 hover:bg-amber-600 text-white shadow-sm" onClick={reset} data-testid="receipt-create-another-button"><Plus className="h-4 w-4 mr-1.5" /> Create another</Button>
          </div>
          <p className="mt-4 text-xs text-slate-500 font-mono break-all">Public link: {process.env.REACT_APP_BACKEND_URL}{result.pdf_url}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <F label="Tenant" span2>
            <Select value={tenantId} onValueChange={onPickTenant}>
              <SelectTrigger data-testid="receipt-form-tenant-select"><SelectValue placeholder="Pick a tenant" /></SelectTrigger>
              <SelectContent className="max-h-64" data-testid="receipt-form-tenant-options">
                {tenants.length === 0 ? (
                  <SelectItem value="__none" disabled>No tenants — add one first</SelectItem>
                ) : sortedTenants.map((t) => (
                  <SelectItem key={t.id} value={t.id}>
                    {t.full_name} — Room {t.room_no} (₹{Number(t.monthly_rent).toLocaleString("en-IN")})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedTenant && (
              <div className="mt-2 rounded-lg bg-indigo-50 border border-indigo-100 px-3 py-2.5 grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs" data-testid="receipt-form-tenant-autofill-card">
                <div><p className="font-semibold uppercase tracking-wider text-slate-400">Room</p><p className="font-medium text-slate-800 mt-0.5">{selectedTenant.room_no}</p></div>
                <div><p className="font-semibold uppercase tracking-wider text-slate-400">Mobile</p><p className="font-medium text-slate-800 mt-0.5 font-mono">{selectedTenant.whatsapp || "—"}</p></div>
                <div><p className="font-semibold uppercase tracking-wider text-slate-400">Rent</p><p className="font-medium text-slate-800 mt-0.5">₹ {Number(selectedTenant.monthly_rent || 0).toLocaleString("en-IN")}</p></div>
                <div><p className="font-semibold uppercase tracking-wider text-slate-400">Deposit (Advance)</p><p className="font-medium text-slate-800 mt-0.5">₹ {Number(selectedTenant.security_deposit || 0).toLocaleString("en-IN")}</p></div>
                <div><p className="font-semibold uppercase tracking-wider text-slate-400">Refundable</p><p className="font-medium text-slate-800 mt-0.5">₹ {Number((selectedTenant.refundable_amount || 0) > 0 ? selectedTenant.refundable_amount : (selectedTenant.security_deposit || 0)).toLocaleString("en-IN")}</p></div>
                <div><p className="font-semibold uppercase tracking-wider text-slate-400">Sharing</p><p className="font-medium text-slate-800 mt-0.5">{selectedTenant.sharing_type || "—"}</p></div>
                <div><p className="font-semibold uppercase tracking-wider text-slate-400">DOJ</p><p className="font-medium text-slate-800 mt-0.5">{selectedTenant.joining_date || "—"}</p></div>
              </div>
            )}
          </F>
          <F label="For the Month of">
            <Input
              type="month"
              value={toMonthValue(monthYear)}
              min={MONTH_BOUNDS.min}
              max={MONTH_BOUNDS.max}
              onChange={(e) => {
                const v = e.target.value;
                if (!v) return;
                if (v < MONTH_BOUNDS.min || v > MONTH_BOUNDS.max) {
                  toast.error("Only the previous 3 months, current month and next month are allowed");
                  return;
                }
                setMonthYear(fromMonthValue(v));
              }}
              data-testid="receipt-form-month-input"
            />
            <p className="text-[11px] text-slate-500">Allowed: previous 2 months, current month, next month · Selected: <span className="font-medium text-slate-700">{monthYear}</span></p>
          </F>
          <F label="Payment date"><Input type="date" value={date} max={todayStr()} onChange={(e) => setDate(e.target.value)} data-testid="receipt-form-date-input" /></F>
          <F label="Payment mode">
            <Select value={mode} onValueChange={setMode}>
              <SelectTrigger data-testid="receipt-form-mode-select"><SelectValue /></SelectTrigger>
              <SelectContent>{PAYMENT_MODES.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
            </Select>
          </F>
          <F label="Amount paid (₹)"><Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="receipt-form-amount-input" /></F>
          <F label="Notes (optional)" span2><Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="e.g. includes ₹500 electricity" data-testid="receipt-form-notes-input" /></F>
          <div className="md:col-span-2 flex justify-end pt-2">
            <Button className="bg-indigo-600 hover:bg-indigo-700 text-white" onClick={submit} disabled={busy} data-testid="receipt-form-generate-button">
              {busy ? "Generating…" : "Generate receipt"}
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}

function F({ label, children, span2 }) {
  return (
    <div className={`space-y-1.5 ${span2 ? "md:col-span-2" : ""}`}>
      <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">{label}</Label>
      {children}
    </div>
  );
}
